import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface MultiOccupationParams {
  primary_occupation: string;
  comparison_occupations: string[];
  area: string;
  user_id?: string;
}

interface OccupationMetrics {
  occupation_code: string;
  occupation_title: string;
  median_salary: number;
  employment: number;
  growth_rate: number;
  similarity_score: number;
  correlation: number;
  current_salary: number;
  current_employment: number;
  growth_5yr: number;
  salary_range: {
    p10: number;
    p25: number;
    median: number;
    p75: number;
    p90: number;
  };
}

interface CorrelationData {
  primary_occ: string;
  comparison_occ: string;
  area_code: string;
  correlation_coefficient: number;
  relative_growth_rate: number;
  skill_premium_differential: number;
  market_share_trends: number;
}

interface Rankings {
  by_salary: OccupationMetrics[];
  by_growth: OccupationMetrics[];
  by_employment: OccupationMetrics[];
}

interface MultiOccupationCompareData {
  primary_occupation: string;
  comparison_metrics: OccupationMetrics[];
  correlations: CorrelationData[];
  rankings: Rankings;
  wage_history: unknown[];
  meta: {
    area: string;
    area_code: string;
    occupation_count: number;
    data_period: string;
    is_pro: boolean;
  };
}

export const useMultiOccupationCompare = (params: MultiOccupationParams) => {
  const { user } = useAuth();

  return useQuery<MultiOccupationCompareData>({
    queryKey: ['multi-occupation-compare', params, user?.id],
    queryFn: async (): Promise<MultiOccupationCompareData> => {
      const { data, error } = await supabase.functions.invoke('api-multi-occupation-compare', {
        body: {
          ...params,
          user_id: user?.id
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to fetch multi-occupation comparison data');
      }

      return data;
    },
    enabled: params.comparison_occupations.length > 0 && params.primary_occupation.length > 0,
    staleTime: 1000 * 60 * 15, // 15 minutes
    gcTime: 1000 * 60 * 60, // 1 hour
  });
};